/**
 * TOSS Web Application — customer-portal permission keys
 *
 * Mirror of `src/Toss.Api/Common/Auth/ClientPermissions/ClientPermissionKeys.cs`, and the client
 * portal's own vocabulary — deliberately NOT an extension of `PK` in `constants/permissions.ts`.
 *
 * The two permission systems are separate on the server (the internal matrix is honoured on
 * `/api/lk/*` whatever the audience, which is exactly why a customer may never hold one of its
 * keys), and they stay separate here. Every key is prefixed `client.`: without it
 * `applications.view` is character-for-character the internal key, and a string copied from one
 * place to the other looks correct.
 *
 * Lives in `constants/` rather than inside `features/client/**` because the navigation config in
 * `components/layout/client-nav/**` gates on these too, and the ESLint boundary (decision Р6)
 * makes importing from the feature folder an error everywhere else.
 *
 * **This is what the UI HIDES by, never what protects anything.** Every write is gated server-side
 * by `[RequireClientCapability]`; hiding a button the server would refuse is a courtesy, and the
 * server refusing it is the rule.
 */

export const CPK = {
  Applications: {
    View: 'client.applications.view',
    Create: 'client.applications.create',
    Cancel: 'client.applications.cancel',
    HistoryView: 'client.applications.history.view',
  },
  Approvals: {
    View: 'client.approvals.view',
    Decide: 'client.approvals.decide',
  },
  Templates: {
    View: 'client.templates.view',
    Manage: 'client.templates.manage',
  },
  Documents: {
    View: 'client.documents.view',
    Download: 'client.documents.download',
  },
  Fleet: {
    View: 'client.fleet.view',
  },
  Chat: {
    View: 'client.chat.view',
    Write: 'client.chat.write',
    /** Starting a chat or a group with colleagues. Every role but the observer holds it by default. */
    Create: 'client.chat.create',
    MembersManage: 'client.chat.members.manage',
  },
  Employees: {
    View: 'client.employees.view',
    /** Also what makes a colleague's email and phone visible — the managed revision of Р2. */
    Manage: 'client.employees.manage',
    RoleAssign: 'client.employees.role.assign',
  },
  Roles: {
    MatrixView: 'client.roles.matrix.view',
    MatrixManage: 'client.roles.matrix.manage',
  },
  Audit: {
    View: 'client.audit.view',
  },
} as const;

type Leaves<T> = T extends string ? T : { [K in keyof T]: Leaves<T[K]> }[keyof T];

/** Every key of the customer portal, as a union — so a typo is a type error. */
export type ClientPermissionKey = Leaves<typeof CPK>;

/**
 * The four roles of a customer's cabinet, mirroring `Data/Enums/ClientRole.cs`.
 *
 * The SET is fixed in code on both sides; only what each role may do is configurable, and only per
 * company. Russian names come from the server (`roleName` on the profile, `name` in the role
 * catalog) so one spelling exists — never translated here.
 */
export const CLIENT_ROLES = {
  Owner: 'Owner',
  Administrator: 'Administrator',
  Employee: 'Employee',
  Observer: 'Observer',
} as const;

export type ClientRole = (typeof CLIENT_ROLES)[keyof typeof CLIENT_ROLES];
